
import { computed, defineComponent, PropType, ref, watch, onMounted, onUpdated } from 'vue'
import { CountryNames, CountriesByName, CountryIso } from '@/constants/country_iso_dict'
import { Address } from '@/models/generics'
import _ from 'lodash'
import { UUID } from '@Protium/vue-ozone-utils'
import { useStore } from 'vuex'
import { useQuasar } from 'quasar'
import { debounce, isUUID } from '@/utils'
import PostalAddress from '@/utils/postalAddress'

export default defineComponent({
  name: 'AddressEditor',
  props: {
    modelValue: {
      type: Object as PropType<Partial<Address>>,
      required: true
    },
    id: {
      type: String as PropType<UUID>
    },
    highlightInvalid: {
      type: Boolean,
      default: true,
    },
    request: {
      type: Function
    },
    editing: {
      type: Boolean
    },
    valid: {
      type: Boolean
    }
  },
  emits: ['update:modelValue', 'save', 'update:valid', 'update:editing'],
  setup (props, { emit }) {
    const $q = useQuasar()
    const $store = useStore()
    const $patch = props.request || $store.getters['Services/Directr'].patch

    watch(() => props.id, value => {
      postalAddress.value = new PostalAddress(props.modelValue)
    })
    watch(() => props.modelValue, value => {
      postalAddress.value = new PostalAddress(props.modelValue)
      //isEditing.value = false
    })
    // const postalAddress = computed(() => new PostalAddress(props.modelValue))
    const postalAddress = ref(new PostalAddress(props.modelValue))

    watch(() => postalAddress.value.value, debounce((value, oldValue) => {
      // console.debug('watcher', _.isEqual(_.pickBy(value, _.identity), _.pickBy(oldValue, _.identity)), _.pickBy(value, _.identity), _.pickBy(oldValue, _.identity))
      if (!_.isEqual(_.pickBy(value, _.identity), _.pickBy(oldValue, _.identity))) emit('update:modelValue', _.pickBy(value, _.identity))
    }, 100))
    const validation_state = computed(() => postalAddress.value.validation_state)

    const editingInternal = ref(props.editing || false)
    const isEditing = computed({
      get: () => props.editing || editingInternal.value,
      set: (value) => {
        editingInternal.value = value
        emit('update:editing', value)
      }
    })

    function updateCountry (country) {
      postalAddress.value._country = country
      postalAddress.value._country_iso = CountriesByName[country].iso.alpha2 as CountryIso
    }

    async function save () {

      if (!Object.values(validation_state.value).every(v => v === true)) {
        $q.notify({
          message: 'Address invalid',
          type: 'negative',
          classes: 'custom-notify warn',
        })
        return
      }

      if (!isUUID(props.id)) {
        // console.warn(`Address Data | Could not save data, the passed "id" prop was not a valid UUID (was ${props.id})`)
        emit('save', postalAddress.value)
        isEditing.value = false
        return
      }
      await $patch(props.id, { shipping_address: postalAddress.value.value }).then(({ items: [updated] }) => {
        emit('update:modelValue', postalAddress.value.value)

        $q.notify({
          message: 'Address updated',
          type: 'positive',
          classes: 'custom-notify success',
        })
      }).catch(e => {
        console.error('Address Editor save error', e)
      }).finally(() => {
        isEditing.value = false
      })
    }

    function edit (value: boolean) {
      isEditing.value = value
    }

    return {
      emit,
      edit,
      save,
      postalAddress,
      validation_state,
      isEditing,
      CountryNames,
      handlers: {
        country: updateCountry,
        save: props.request
      },
    }
  },
})
